import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputLeftAddon,
  Stack,
  Textarea,
} from '@chakra-ui/react';
import CustomModal from 'components/modal/Modal';
import { Toolbox } from 'lucide-react';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { CreateServiceItemDto, ServiceItem } from 'types/models';

const schema = yup.object({
  nombre:      yup.string().required('Nombre requerido'),
  precioBase:  yup.number().typeError('Debe ser un número').min(0, 'Precio >= 0').required('Precio requerido'),
  descripcion: yup.string().optional(),
});

type FormValues = {
  nombre: string;
  precioBase: number;
  descripcion?: string;
};

function toServiceCode(name: string): string {
  const body = name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9\s]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 16); // max 20 total: "SRV-" (4) + 16
  return `SRV-${body}`;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CreateServiceItemDto) => Promise<void>;
  service?: ServiceItem | null;
  isSaving?: boolean;
}

export default function ServiceItemModal({ isOpen, onClose, onSave, service, isSaving = false }: Props) {
  const isEdit = !!service;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: { nombre: '', precioBase: 0, descripcion: '' },
  });

  useEffect(() => {
    if (isOpen) {
      reset(
        service
          ? { nombre: service.name, precioBase: service.basePrice, descripcion: service.description ?? '' }
          : { nombre: '', precioBase: 0, descripcion: '' }
      );
    }
  }, [isOpen, service, reset]);

  const onSubmit = handleSubmit(async (values) => {
    await onSave({
      name:        values.nombre,
      code:        isEdit ? service!.code : toServiceCode(values.nombre),
      basePrice:   values.precioBase,
      description: values.descripcion || undefined,
    });
  });

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Toolbox size={20} />
          {isEdit ? 'Editar Servicio' : 'Nuevo Servicio'}
        </span>
      }
      primaryBtnLabel={isEdit ? 'Guardar Cambios' : 'Crear Servicio'}
      primaryBtnLoading={isSaving}
      onPrimaryClick={() => onSubmit()}
      size='md'
    >
      <Stack spacing='4' as='form' id='service-form' onSubmit={onSubmit}>
        <FormControl isInvalid={!!errors.nombre} isRequired>
          <FormLabel fontSize='sm' fontWeight='600'>Nombre del Servicio</FormLabel>
          <Input {...register('nombre')} placeholder='Ej. Evaluación Inicial' />
          <FormErrorMessage>{errors.nombre?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors.precioBase} isRequired>
          <FormLabel fontSize='sm' fontWeight='600'>Precio Base</FormLabel>
          <InputGroup>
            <InputLeftAddon>$</InputLeftAddon>
            <Input
              {...register('precioBase')}
              type='number'
              step='0.01'
              min={0}
              placeholder='0.00'
            />
          </InputGroup>
          <FormErrorMessage>{errors.precioBase?.message}</FormErrorMessage>
        </FormControl>

        <FormControl>
          <FormLabel fontSize='sm' fontWeight='600'>
            Descripción{' '}
            <span style={{ fontWeight: 400, color: 'gray' }}>(opcional)</span>
          </FormLabel>
          <Textarea
            {...register('descripcion')}
            placeholder='Descripción breve del servicio...'
            rows={3}
            resize='none'
          />
        </FormControl>
      </Stack>
    </CustomModal>
  );
}
